import { useContext, useEffect, useRef, useState } from "react";

import {
  langMutationFunctions,
  slngMutationFunctions,
  enrlMutationFunctions,
  clenMutationFunctions,
  mutationFunctions as chldMutationFunctions,
  type CHLD,
} from "@/models/chld";
import { mutationFunctions as famiMutationFunctions } from "@/models/fami";
import { type FAMI } from "@/models/fami";
import {
  type LANG,
  type SLNG,
  type ENRL,
  type CLEN,
  type MutationResponse,
} from "@/models";
import { useLeaData } from "@/queries.ts";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";

type LeaData = ReturnType<typeof useLeaData>["data"];

export const makeChldId = (c: CHLD) =>
  `${c.ProgramYear}|${c.PreschoolCode}|${c.LocalChildId}|${c.CSPPID}|${c.EffectiveStartDate ?? ""}`;

export const makeFamiId = (f: FAMI) =>
  `${f.ProgramYear}|${f.PreschoolCode}|${f.LocalFamilyId}|${f.EffectiveStartDate ?? ""}`;

export const makeLangId = (l: LANG) =>
  `${l.ProgramYear}|${l.PreschoolCode}|${l.LocalChildId}|${l.CSPPID}|${l.EffectiveStartDate ?? ""}`;

export const makeSlngId = (s: SLNG) =>
  `${s.ProgramYear}|${s.PreschoolCode}|${s.LocalChildId}|${s.CSPPID}|${s.EffectiveStartDate ?? ""}`;

export const makeEnrlId = (e: ENRL) =>
  `${e.ProgramYear}|${e.PreschoolCode}|${e.LocalChildId}|${e.CSPPID}|${e.EnrollmentStartDate ?? ""}`;

export const makeClenId = (c: CLEN) => c.Id;

export function useBlockedChildDeletion({
  leaData,
  chldRecords = [],
  famiRecords,
  langRecords,
  slngRecords,
  enrlRecords,
  clenRecords,
  blockedDeletionOpen,
}: {
  leaData: LeaData;
  chldRecords?: CHLD[];
  famiRecords: FAMI[];
  langRecords: LANG[];
  slngRecords: SLNG[];
  enrlRecords: ENRL[];
  clenRecords: CLEN[];
  blockedDeletionOpen: boolean;
}) {
  const { addPendingFileSubmission, pendingFileSubmissions, showToast } =
    useContext(FileSubmissionsContext);

  const [pendingChldDeletes, setPendingChldDeletes] = useState<Set<string>>(
    new Set()
  );
  const [pendingFamiDeletes, setPendingFamiDeletes] = useState<Set<string>>(
    new Set()
  );
  const [pendingLangDeletes, setPendingLangDeletes] = useState<Set<string>>(
    new Set()
  );
  const [pendingSlngDeletes, setPendingSlngDeletes] = useState<Set<string>>(
    new Set()
  );
  const [pendingEnrlDeletes, setPendingEnrlDeletes] = useState<Set<string>>(
    new Set()
  );
  const [pendingClenDeletes, setPendingClenDeletes] = useState<Set<string>>(
    new Set()
  );

  const [isChldBulkSubmitting, setIsChldBulkSubmitting] = useState(false);
  const [isFamiBulkSubmitting, setIsFamiBulkSubmitting] = useState(false);
  const [isLangBulkSubmitting, setIsLangBulkSubmitting] = useState(false);
  const [isSlngBulkSubmitting, setIsSlngBulkSubmitting] = useState(false);
  const [isEnrlBulkSubmitting, setIsEnrlBulkSubmitting] = useState(false);
  const [isClenBulkSubmitting, setIsClenBulkSubmitting] = useState(false);

  const [pendingBulkDeleteIds, setPendingBulkDeleteIds] = useState<Set<string>>(
    new Set()
  );

  const chldBulkResultHandler = useRef<
    ((r: MutationResponse<CHLD>) => void) | undefined
  >(undefined);
  const famiBulkResultHandler = useRef<
    ((r: MutationResponse<FAMI>) => void) | undefined
  >(undefined);
  const langBulkResultHandler = useRef<
    ((r: MutationResponse<LANG>) => void) | undefined
  >(undefined);
  const slngBulkResultHandler = useRef<
    ((r: MutationResponse<SLNG>) => void) | undefined
  >(undefined);
  const enrlBulkResultHandler = useRef<
    ((r: MutationResponse<ENRL>) => void) | undefined
  >(undefined);
  const clenBulkResultHandler = useRef<
    ((r: MutationResponse<CLEN>) => void) | undefined
  >(undefined);

  const chldBulk = chldMutationFunctions(leaData, (r) =>
    chldBulkResultHandler.current?.(r)
  );
  const famiBulk = famiMutationFunctions(leaData, (r) =>
    famiBulkResultHandler.current?.(r)
  );
  const langBulk = langMutationFunctions(leaData, (r) =>
    langBulkResultHandler.current?.(r)
  );
  const slngBulk = slngMutationFunctions(leaData, (r) =>
    slngBulkResultHandler.current?.(r)
  );
  const enrlBulk = enrlMutationFunctions(leaData, (r) =>
    enrlBulkResultHandler.current?.(r)
  );
  const clenBulk = clenMutationFunctions(leaData, (r) =>
    clenBulkResultHandler.current?.(r)
  );

  useEffect(() => {
    setPendingChldDeletes((prev) => {
      const live = new Set(chldRecords.map(makeChldId));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [chldRecords]);

  useEffect(() => {
    setPendingFamiDeletes((prev) => {
      const live = new Set(famiRecords.map(makeFamiId));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [famiRecords]);

  useEffect(() => {
    setPendingLangDeletes((prev) => {
      const live = new Set(langRecords.map(makeLangId));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [langRecords]);

  useEffect(() => {
    setPendingSlngDeletes((prev) => {
      const live = new Set(slngRecords.map(makeSlngId));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [slngRecords]);

  useEffect(() => {
    setPendingEnrlDeletes((prev) => {
      const live = new Set(enrlRecords.map(makeEnrlId));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [enrlRecords]);

  useEffect(() => {
    setPendingClenDeletes((prev) => {
      const live = new Set(clenRecords.map(makeClenId));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [clenRecords]);

  const anyBulkSubmitting =
    isChldBulkSubmitting ||
    isFamiBulkSubmitting ||
    isLangBulkSubmitting ||
    isSlngBulkSubmitting ||
    isEnrlBulkSubmitting ||
    isClenBulkSubmitting;

  const sawBulkActivityRef = useRef(false);

  useEffect(() => {
    if (anyBulkSubmitting || pendingBulkDeleteIds.size > 0) {
      sawBulkActivityRef.current = true;
      return;
    }
    if (!sawBulkActivityRef.current) return;
    sawBulkActivityRef.current = false;
    setPendingChldDeletes((prev) => (prev.size === 0 ? prev : new Set()));
    setPendingFamiDeletes((prev) => (prev.size === 0 ? prev : new Set()));
    setPendingLangDeletes((prev) => (prev.size === 0 ? prev : new Set()));
    setPendingSlngDeletes((prev) => (prev.size === 0 ? prev : new Set()));
    setPendingEnrlDeletes((prev) => (prev.size === 0 ? prev : new Set()));
    setPendingClenDeletes((prev) => (prev.size === 0 ? prev : new Set()));
  }, [anyBulkSubmitting, pendingBulkDeleteIds]);

  useEffect(() => {
    if (!blockedDeletionOpen) return;
    setPendingBulkDeleteIds((prev) => {
      const live = new Set(
        (pendingFileSubmissions ?? []).map((s: { Id: string | number }) =>
          String(s.Id)
        )
      );
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [pendingFileSubmissions, blockedDeletionOpen]);

  useEffect(() => {
    if (!blockedDeletionOpen) return;
    const relatedIds = (pendingFileSubmissions ?? [])
      .filter(
        (fs: { RecordTypeCode?: string }) =>
          fs.RecordTypeCode === "CHLD" ||
          fs.RecordTypeCode === "FAMI" ||
          fs.RecordTypeCode === "LANG" ||
          fs.RecordTypeCode === "SLNG" ||
          fs.RecordTypeCode === "ENRL" ||
          fs.RecordTypeCode === "CLEN"
      )
      .map((fs: { Id: string | number }) => String(fs.Id));
    if (relatedIds.length === 0) return;
    setPendingBulkDeleteIds((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const id of relatedIds) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pendingFileSubmissions, blockedDeletionOpen]);

  const addChldDelete = (id: string) =>
    setPendingChldDeletes((s) => new Set(s).add(id));
  const addFamiDelete = (id: string) =>
    setPendingFamiDeletes((s) => new Set(s).add(id));
  const addLangDelete = (id: string) =>
    setPendingLangDeletes((s) => new Set(s).add(id));
  const addSlngDelete = (id: string) =>
    setPendingSlngDeletes((s) => new Set(s).add(id));
  const addEnrlDelete = (id: string) =>
    setPendingEnrlDeletes((s) => new Set(s).add(id));
  const addClenDelete = (id: string) =>
    setPendingClenDeletes((s) => new Set(s).add(id));

  const removeFromPending = (
    setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    ids: string[]
  ) =>
    setter((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of ids) {
        if (next.delete(id)) changed = true;
      }
      return changed ? next : prev;
    });

  const handleBlockedDeleteRecords = async (
    groupKey: string,
    recordIds: string[]
  ) => {
    type GroupCfg<R> = {
      records: R[];
      idFor: (r: R) => string;
      bulk: { handleSave: (r: R, isDelete: boolean) => void };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handlerRef: React.MutableRefObject<any>;
      addPending: (id: string) => void;
      removePending: (ids: string[]) => void;
      setSubmitting: (v: boolean) => void;
      recordKind: string;
      recordKindLower: string;
    };

    const byGroup: Record<string, GroupCfg<unknown>> = {
      chld: {
        records: chldRecords,
        idFor: (r) => makeChldId(r as CHLD),
        bulk: chldBulk as unknown as GroupCfg<unknown>["bulk"],
        handlerRef: chldBulkResultHandler,
        addPending: addChldDelete,
        removePending: (ids) => removeFromPending(setPendingChldDeletes, ids),
        setSubmitting: setIsChldBulkSubmitting,
        recordKind: "Child",
        recordKindLower: "child",
      },
      fami: {
        records: famiRecords,
        idFor: (r) => makeFamiId(r as FAMI),
        bulk: famiBulk as unknown as GroupCfg<unknown>["bulk"],
        handlerRef: famiBulkResultHandler,
        addPending: addFamiDelete,
        removePending: (ids) => removeFromPending(setPendingFamiDeletes, ids),
        setSubmitting: setIsFamiBulkSubmitting,
        recordKind: "Family",
        recordKindLower: "family",
      },
      lang: {
        records: langRecords,
        idFor: (r) => makeLangId(r as LANG),
        bulk: langBulk as unknown as GroupCfg<unknown>["bulk"],
        handlerRef: langBulkResultHandler,
        addPending: addLangDelete,
        removePending: (ids) => removeFromPending(setPendingLangDeletes, ids),
        setSubmitting: setIsLangBulkSubmitting,
        recordKind: "Language",
        recordKindLower: "language",
      },
      slng: {
        records: slngRecords,
        idFor: (r) => makeSlngId(r as SLNG),
        bulk: slngBulk as unknown as GroupCfg<unknown>["bulk"],
        handlerRef: slngBulkResultHandler,
        addPending: addSlngDelete,
        removePending: (ids) => removeFromPending(setPendingSlngDeletes, ids),
        setSubmitting: setIsSlngBulkSubmitting,
        recordKind: "Supplemental language",
        recordKindLower: "supplemental language",
      },
      enrl: {
        records: enrlRecords,
        idFor: (r) => makeEnrlId(r as ENRL),
        bulk: enrlBulk as unknown as GroupCfg<unknown>["bulk"],
        handlerRef: enrlBulkResultHandler,
        addPending: addEnrlDelete,
        removePending: (ids) => removeFromPending(setPendingEnrlDeletes, ids),
        setSubmitting: setIsEnrlBulkSubmitting,
        recordKind: "Enrollment",
        recordKindLower: "enrollment",
      },
      clen: {
        records: clenRecords,
        idFor: (r) => makeClenId(r as CLEN),
        bulk: clenBulk as unknown as GroupCfg<unknown>["bulk"],
        handlerRef: clenBulkResultHandler,
        addPending: addClenDelete,
        removePending: (ids) => removeFromPending(setPendingClenDeletes, ids),
        setSubmitting: setIsClenBulkSubmitting,
        recordKind: "Classroom enrollment",
        recordKindLower: "classroom enrollment",
      },
    };

    const cfg = byGroup[groupKey];
    if (!cfg) return;
    // Re-entrancy guard: if a previous batch for this group hasn't finished
    // resolving (handlerRef still set), bail out. Otherwise the new closure
    // would replace the old one and the previous batch's success/processing
    // toasts would never fire. Currently the dialog's "Delete selected" is
    // gated on deletingRecords so this is hard to trigger, but the guard
    // makes the invariant explicit.
    if (cfg.handlerRef.current) return;

    const liveById = new Map(cfg.records.map((r) => [cfg.idFor(r), r]));
    const targets = recordIds
      .map((id) => liveById.get(id))
      .filter((r): r is unknown => r !== undefined);
    if (targets.length === 0) return;

    const sentIds = targets.map((r) => cfg.idFor(r));

    cfg.setSubmitting(true);

    const total = targets.length;
    const processingMsg = `${total} ${cfg.recordKindLower} record${total !== 1 ? "s" : ""} submitted for deletion`;
    const successMsg = `${cfg.recordKind} record${total !== 1 ? "s" : ""} deleted successfully`;

    type AnyFileSubmission = NonNullable<
      MutationResponse<FAMI>["fileSubmission"]
    >;
    const successfulSubmissions: AnyFileSubmission[] = [];
    let anyValidationFailed = false;
    let received = 0;

    cfg.handlerRef.current = (
      result:
        | MutationResponse<CHLD>
        | MutationResponse<FAMI>
        | MutationResponse<LANG>
        | MutationResponse<SLNG>
        | MutationResponse<ENRL>
        | MutationResponse<CLEN>
    ) => {
      received += 1;
      if (result.fileSubmission) {
        successfulSubmissions.push(result.fileSubmission as AnyFileSubmission);
      } else {
        anyValidationFailed = true;
      }

      if (received === total) {
        const submittedIds = successfulSubmissions.map((s) => String(s.Id));
        if (submittedIds.length > 0) {
          setPendingBulkDeleteIds(
            (prev) => new Set([...prev, ...submittedIds])
          );
        }
        successfulSubmissions.forEach((sub, i) =>
          addPendingFileSubmission(sub, i === 0 ? successMsg : undefined)
        );
        if (successfulSubmissions.length > 0) {
          showToast(
            "processing",
            "Processing",
            processingMsg,
            `processing-${groupKey}`
          );
        }
        if (anyValidationFailed) {
          showToast(
            "error",
            "Error",
            `Some ${cfg.recordKindLower} records could not be submitted for deletion.`
          );

          cfg.removePending(sentIds);
        }
        cfg.setSubmitting(false);
        cfg.handlerRef.current = undefined;
      }
    };

    for (const r of targets) {
      cfg.addPending(cfg.idFor(r));
      cfg.bulk.handleSave(r, true);
    }
  };

  const reset = () => {
    setPendingChldDeletes(new Set());
    setPendingFamiDeletes(new Set());
    setPendingLangDeletes(new Set());
    setPendingSlngDeletes(new Set());
    setPendingEnrlDeletes(new Set());
    setPendingClenDeletes(new Set());
    setPendingBulkDeleteIds(new Set());
    setIsChldBulkSubmitting(false);
    setIsFamiBulkSubmitting(false);
    setIsLangBulkSubmitting(false);
    setIsSlngBulkSubmitting(false);
    setIsEnrlBulkSubmitting(false);
    setIsClenBulkSubmitting(false);
  };

  return {
    pendingChldDeletes,
    pendingFamiDeletes,
    pendingLangDeletes,
    pendingSlngDeletes,
    pendingEnrlDeletes,
    pendingClenDeletes,
    addChldDelete,
    addFamiDelete,
    addLangDelete,
    addSlngDelete,
    addEnrlDelete,
    addClenDelete,
    handleBlockedDeleteRecords,
    deletingRecords:
      isChldBulkSubmitting ||
      isFamiBulkSubmitting ||
      isLangBulkSubmitting ||
      isSlngBulkSubmitting ||
      isEnrlBulkSubmitting ||
      isClenBulkSubmitting ||
      pendingBulkDeleteIds.size > 0,
    reset,
  };
}
